import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// utils and helpers
import { searchFilterByFields } from "../utils/searchFilterExpenses";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchLevies } from "@/services/financeApi";
import { useBusiness } from "@hooks/useBusiness";

export default function useLeviesTable(searchTerm = "") {
  const { businessId } = useBusiness();

  const {
    data: levies = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // Levies live on settings.financial.levies, not in their own collection.
    queryKey: ["finance-levies", businessId],
    queryFn: () => fetchLevies(businessId),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const filteredLevies = useMemo(
    () => searchFilterByFields(levies, searchTerm, ["name", "type", "appliesTo"]),
    [levies, searchTerm]
  );

  const levyColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Levy",
        cell: ({ row }) => <span className="font-medium text-[#1a1a1a]">{row.original.name}</span>,
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <span className="capitalize text-[#555]">{row.original.type || "-"}</span>
        ),
      },
      {
        accessorKey: "amount",
        header: "Rate / Amount",
        cell: ({ row }) => {
          const { type, amount } = row.original;
          if (type === "percentage") return `${amount}%`;
          return formatCurrency(amount);
        },
      },
      {
        accessorKey: "appliesTo",
        header: "Applies To",
        cell: ({ row }) => row.original.appliesTo || "-",
      },
    ],
    []
  );

  const renderLevyRow = useCallback((row) => {
    return (
      <TableRow key={row.id} className="hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
            {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
  }, []);

  return {
    allLevies: levies,
    levies: filteredLevies,
    levyColumns,
    renderLevyRow,
    refetch,
    isLoading,
    isError,
  };
}
